<script setup>
import { computed } from 'vue'
import { trans } from 'laravel-vue-i18n'

const props = defineProps({
    currentMonth: Object, // moment object
    reservations: { type: Array, default: () => [] },
    visibleFormIds: { type: Set, default: () => new Set() },
})

const emit = defineEmits(['prev', 'next', 'selectDay'])

const DOW = [
    trans('common.dow_short.mon'),
    trans('common.dow_short.tue'),
    trans('common.dow_short.wed'),
    trans('common.dow_short.thu'),
    trans('common.dow_short.fri'),
    trans('common.dow_short.sat'),
    trans('common.dow_short.sun'),
]

const MONTHS = [
    trans('common.months_gen.jan'),
    trans('common.months_gen.feb'),
    trans('common.months_gen.mar'),
    trans('common.months_gen.apr'),
    trans('common.months_gen.may'),
    trans('common.months_gen.jun'),
    trans('common.months_gen.jul'),
    trans('common.months_gen.aug'),
    trans('common.months_gen.sep'),
    trans('common.months_gen.oct'),
    trans('common.months_gen.nov'),
    trans('common.months_gen.dec'),
]

const monthName = computed(() => {
    return MONTHS[props.currentMonth.month()] + ' ' + props.currentMonth.year()
})

const cells = computed(() => {
    const m = props.currentMonth.clone()
    const firstDow = m.clone().startOf('month').isoWeekday() // 1=Po, 7=Ne
    const daysInMonth = m.daysInMonth()
    const result = []

    for (let i = 1; i < firstDow; i++) result.push(null)
    for (let d = 1; d <= daysInMonth; d++) result.push(d)
    while (result.length % 7 !== 0) result.push(null)

    return result
})

const today = new Date()

function isToday(d) {
    if (!d) return false
    return d === today.getDate()
        && props.currentMonth.month() === today.getMonth()
        && props.currentMonth.year() === today.getFullYear()
}

function hasEvent(d) {
    if (!d) return false
    const dateStr = props.currentMonth.clone().date(d).format('YYYY-MM-DD')
    return props.reservations.some(r => {
        if (!props.visibleFormIds.has(r.id_reservation_forms)) return false
        if (r.date === dateStr) return true
        if (r.date_start && r.date_end) {
            return dateStr >= r.date_start && dateStr <= r.date_end
        }
        return false
    })
}
</script>

<template>
    <div class="px-1 pb-1">
        <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-semibold text-neutral-800 dark:text-neutral-100">{{ monthName }}</span>
            <div class="flex gap-0.5">
                <button
                    class="w-6 h-6 rounded flex items-center justify-center text-neutral-400 dark:text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-200"
                    @click="emit('prev')"
                >
                    <i class="pi pi-chevron-left !text-[10px]"/>
                </button>
                <button
                    class="w-6 h-6 rounded flex items-center justify-center text-neutral-400 dark:text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-200"
                    @click="emit('next')"
                >
                    <i class="pi pi-chevron-right !text-[10px]"/>
                </button>
            </div>
        </div>

        <div class="grid grid-cols-7 gap-px">
            <div
                v-for="d in DOW"
                :key="d"
                class="text-center text-[9px] font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider py-1"
            >
                {{ d }}
            </div>
            <div
                v-for="(day, i) in cells"
                :key="i"
                class="relative h-[26px] flex items-center justify-center text-[11px] rounded-full cursor-default"
                :class="{
                    'bg-blue-600 text-white font-semibold': isToday(day),
                    'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800': day && !isToday(day),
                    'text-transparent pointer-events-none': !day,
                }"
                @click="day && emit('selectDay', day)"
            >
                {{ day ?? '' }}
                <span
                    v-if="hasEvent(day) && !isToday(day)"
                    class="absolute bottom-[3px] left-1/2 -translate-x-1/2 w-[3px] h-[3px] rounded-full bg-blue-600"
                />
                <span
                    v-if="hasEvent(day) && isToday(day)"
                    class="absolute bottom-[3px] left-1/2 -translate-x-1/2 w-[3px] h-[3px] rounded-full bg-white"
                />
            </div>
        </div>
    </div>
</template>
